"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/AuthContext"
import { useTheme } from "../contexts/ThemeContext"
import axios from "axios"
import { getApiUrl, API_ENDPOINTS } from "../config/api"
import ImageViewer from "./ImageViewer"
import Toast from "./Toast"
import { useToast } from "../hooks/useToast"

// Common constants to avoid repetition
const TAB_NAMES = {
  PROMPT_TO_IMAGE: "prompt-to-image",
  TRANSFORM_IMAGE: "image-to-image", // Keep internal value same for API compatibility
  IMAGE_TO_VIDEO: "image-to-video"
}

const DISPLAY_NAMES = {
  [TAB_NAMES.PROMPT_TO_IMAGE]: "Prompt to Image",
  [TAB_NAMES.TRANSFORM_IMAGE]: "Transform Image",
  [TAB_NAMES.IMAGE_TO_VIDEO]: "Transform to Video"
}

const CREDIT_COSTS = {
  [TAB_NAMES.PROMPT_TO_IMAGE]: 10,
  [TAB_NAMES.TRANSFORM_IMAGE]: 120,
  [TAB_NAMES.IMAGE_TO_VIDEO]: {
    "480p": 160,
    "720p": 350
  }
}

// Helper function to get credit cost for current tab and settings
const getCreditCost = (tab, resolution = null) => {
  if (tab === TAB_NAMES.IMAGE_TO_VIDEO) {
    return CREDIT_COSTS[tab][resolution] || CREDIT_COSTS[tab]["480p"];
  }
  return CREDIT_COSTS[tab];
}

function Dashboard() {
  const [prompt, setPrompt] = useState("")
  const [loading, setLoading] = useState(false)
  const [generatedImage, setGeneratedImage] = useState(null)
  const [generations, setGenerations] = useState([])
  const [stats, setStats] = useState(null)
  const [selectedImage, setSelectedImage] = useState(null)
  const [brokenImages, setBrokenImages] = useState({})
  const { user, refreshUser } = useAuth()
  const { theme } = useTheme();
  const navigate = useNavigate()
  const { toasts, showError, showSuccess, hideToast } = useToast();

  // Additional image options state (for PRO users)
  const [goFast, setGoFast] = useState(user?.subscription_type === "pro" ? false : true)
  const [megapixels, setMegapixels] = useState("1")
  const numOutputs = 1  // Keep for potential future use
  const [aspectRatio, setAspectRatio] = useState("1:1")
  const [outputFormat, setOutputFormat] = useState(user?.subscription_type === "pro" ? "jpg" : "webp")
  const [outputQuality, setOutputQuality] = useState(user?.subscription_type === "pro" ? 100 : 80)
  const [numInferenceSteps, setNumInferenceSteps] = useState(4)
  const [inputImage, setInputImage] = useState(null);
  const [lastImage, setLastImage] = useState(null);
  const [resolution, setResolution] = useState("480p");
  const [selectedTab, setSelectedTab] = useState(TAB_NAMES.PROMPT_TO_IMAGE);

  const isProUser = user?.subscription_type === "pro"
  const creditBalance = typeof user?.credit_balance === "number" ? user.credit_balance : (user?.credit_balance || 0)
  const subscriptionType = user?.subscription_type || (user?.subscription_status ? "pro" : "free")

  useEffect(() => {
    fetchGenerations()
    fetchStats()
  }, [])

  const fetchGenerations = async () => {
    try {
      const response = await axios.get(getApiUrl(API_ENDPOINTS.GENERATIONS.LIST))
      setGenerations(response.data)
    } catch (error) { 
      console.error("Failed to fetch generations:", error)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await axios.get(getApiUrl(API_ENDPOINTS.GENERATIONS.STATS))
      setStats(response.data)
    } catch (error) {
      console.error("Failed to fetch stats:", error)
    }
  }

  const handleGenerate = async (e) => {
    e.preventDefault()

    if (!prompt.trim()) {
      showError("Please enter a prompt to generate content");
      return
    }

    // Validate required image uploads for different tabs
    if (selectedTab === TAB_NAMES.TRANSFORM_IMAGE && !inputImage) {
      showError("Please upload an image to transform");
      return
    }

    if (selectedTab === TAB_NAMES.IMAGE_TO_VIDEO && !inputImage) {
      showError("Please upload a first frame image to create a video");
      return
    }

    setLoading(true)
    try {
      const formData = new FormData();
      formData.append('prompt', prompt.trim());

      // Determine which API endpoint to use and prepare data accordingly
      let apiEndpoint;
      
      if (selectedTab === TAB_NAMES.PROMPT_TO_IMAGE) {
        apiEndpoint = API_ENDPOINTS.GENERATIONS.PROMPT_TO_IMAGE;
        // Add PRO user options for prompt-to-image
        if (isProUser) {
          formData.append('go_fast', goFast);
          formData.append('megapixels', megapixels);
          formData.append('num_outputs', numOutputs);
          formData.append('aspect_ratio', aspectRatio);
          formData.append('output_format', outputFormat);
          formData.append('output_quality', outputQuality);
          formData.append('num_inference_steps', numInferenceSteps);
        }
      } else if (selectedTab === TAB_NAMES.TRANSFORM_IMAGE) {
        apiEndpoint = API_ENDPOINTS.GENERATIONS.TRANSFORM_IMAGE;
        formData.append('input_image', inputImage);
        formData.append('aspect_ratio', aspectRatio);
        formData.append('output_format', outputFormat);
      } else if (selectedTab === TAB_NAMES.IMAGE_TO_VIDEO) {
        apiEndpoint = API_ENDPOINTS.GENERATIONS.TRANSFORM_TO_VIDEO;
        formData.append('image', inputImage);
        if (lastImage) formData.append('last_image', lastImage);
        formData.append('resolution', resolution);
      }

      const response = await axios.post(getApiUrl(apiEndpoint), formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setGeneratedImage(response.data)
      fetchGenerations()
      fetchStats()
      refreshUser()
      setPrompt("")
      setInputImage(null)
      setLastImage(null)
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Generation failed";
      showError(errorMessage);

      if (error.response?.status === 403) {
        // Free tier limit reached
        navigate("/subscription")
      }
    } finally {
      setLoading(false)
    }
  }

  // Helper function to check if URL is a video
  const isVideo = (url) => {
    if (!url) return false;
    return url.includes('.mp4') || url.includes('.webm') || url.includes('.mov') || 
           url.includes('video') || url.includes('.avi') || url.includes('.mkv') ||
           url.toLowerCase().includes('video');
  }

  // Helper function to download file
  const downloadFile = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to direct link if fetch fails
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  const getRemainingCredits = () => {
    return user?.credit_balance || 0
  }

  const handleImageClick = (image) => {
    setSelectedImage(image)
  }

  const closeImageViewer = () => {
    setSelectedImage(null)
  }

  // Add delete handler for generated images
  const handleDeleteGeneration = async (generationId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this image? This action cannot be undone.");
    if (!confirmDelete) return;
    
    try {
      await axios.delete(getApiUrl(API_ENDPOINTS.GENERATIONS.DELETE.replace(":id", generationId)));
      setGenerations(generations.filter(g => g.id !== generationId));
      
      showSuccess("Image deleted successfully");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to delete image";
      showError(errorMessage);
    }
  };

  return (
    <div className={theme === "dark" ? "min-h-screen bg-black text-white" : "min-h-screen bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100"}>
      <div className="max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Tab Navigation */}
          <div className="mb-8 flex gap-4 justify-center">
            <button
              className={`px-6 py-2 rounded-xl font-semibold transition-colors border ${selectedTab === TAB_NAMES.PROMPT_TO_IMAGE
                ? (theme === "dark" ? "bg-indigo-700 text-white border-indigo-700" : "bg-indigo-100 text-indigo-700 border-indigo-300")
                : (theme === "dark" ? "bg-gray-900 text-gray-300 border-gray-700 hover:bg-gray-800" : "bg-white text-indigo-700 border-indigo-100 hover:bg-indigo-50")}`}
              onClick={() => setSelectedTab(TAB_NAMES.PROMPT_TO_IMAGE)}
            >
              {DISPLAY_NAMES[TAB_NAMES.PROMPT_TO_IMAGE]}
            </button>
            <button
              className={`px-6 py-2 rounded-xl font-semibold transition-colors border ${selectedTab === TAB_NAMES.TRANSFORM_IMAGE
                ? (theme === "dark" ? "bg-indigo-700 text-white border-indigo-700" : "bg-indigo-100 text-indigo-700 border-indigo-300")
                : (theme === "dark" ? "bg-gray-900 text-gray-300 border-gray-700 hover:bg-gray-800" : "bg-white text-indigo-700 border-indigo-100 hover:bg-indigo-50")}`}
              onClick={() => setSelectedTab(TAB_NAMES.TRANSFORM_IMAGE)}
            >
              {DISPLAY_NAMES[TAB_NAMES.TRANSFORM_IMAGE]}
            </button>
            <button
              className={`px-6 py-2 rounded-xl font-semibold transition-colors border ${selectedTab === TAB_NAMES.IMAGE_TO_VIDEO
                ? (theme === "dark" ? "bg-indigo-700 text-white border-indigo-700" : "bg-indigo-100 text-indigo-700 border-indigo-300")
                : (theme === "dark" ? "bg-gray-900 text-gray-300 border-gray-700 hover:bg-gray-800" : "bg-white text-indigo-700 border-indigo-100 hover:bg-indigo-50")}`}
              onClick={() => setSelectedTab(TAB_NAMES.IMAGE_TO_VIDEO)}
            >
              {DISPLAY_NAMES[TAB_NAMES.IMAGE_TO_VIDEO]}
            </button>
          </div>
          {/* Current Status */}
          <div className={theme === "dark" ? "bg-gray-900 backdrop-blur-md rounded-2xl shadow-xl border border-gray-700 p-8 mb-12" : "bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-indigo-100 p-8 mb-12"}>
            <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-100 mb-6 flex items-center gap-2" : "text-2xl font-bold text-indigo-700 mb-6 flex items-center gap-2"}>
              <svg className={theme === "dark" ? "w-6 h-6 text-gray-300" : "w-6 h-6 text-indigo-500"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
              Your Studio Stats
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className={theme === "dark" ? "text-3xl font-extrabold text-gray-100 drop-shadow" : "text-3xl font-extrabold text-indigo-600 drop-shadow"}>{user?.generation_count || stats?.total_generations || 0}</div>
                <div className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>Images Generated</div>
              </div>
              <div className="text-center">
                <div className={theme === "dark" ? "text-3xl font-extrabold text-green-400 drop-shadow" : "text-3xl font-extrabold text-green-600 drop-shadow"}>{creditBalance}</div>
                <div className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>Credits Remaining</div>
              </div>
              <div className="text-center">
                <div className={theme === "dark" ? "text-3xl font-extrabold text-pink-400 drop-shadow" : "text-3xl font-extrabold text-pink-600 drop-shadow"}>
                  {Math.max(0, Math.floor(creditBalance / getCreditCost(selectedTab, resolution)))}
                </div>
                <div className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>
                  {selectedTab === TAB_NAMES.IMAGE_TO_VIDEO ? 'Videos' : 'Images'} Available
                </div>
              </div>
              <div className="text-center">
                <div className={theme === "dark" ? "text-3xl font-extrabold text-gray-300 drop-shadow capitalize" : "text-3xl font-extrabold text-indigo-500 drop-shadow capitalize"}>{subscriptionType}</div>
                <div className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-500"}>Subscription</div>
              </div>
            </div>
            <div className={theme === "dark" ? "mt-6 p-4 bg-gray-800 rounded-xl shadow border border-gray-700" : "mt-6 p-4 bg-blue-50 rounded-xl shadow"}>
              <p className={theme === "dark" ? "text-sm text-gray-300 text-center" : "text-sm text-blue-800 text-center"}>
                💡 <strong>Credit System:</strong> {
                  selectedTab === TAB_NAMES.TRANSFORM_IMAGE 
                    ? `${CREDIT_COSTS[TAB_NAMES.TRANSFORM_IMAGE]} credits = 1 ${DISPLAY_NAMES[TAB_NAMES.TRANSFORM_IMAGE].toLowerCase()} generation`
                    : selectedTab === TAB_NAMES.IMAGE_TO_VIDEO
                    ? `${getCreditCost(TAB_NAMES.IMAGE_TO_VIDEO, resolution)} credits = 1 ${DISPLAY_NAMES[TAB_NAMES.IMAGE_TO_VIDEO].toLowerCase()} (${resolution})`
                    : `${CREDIT_COSTS[TAB_NAMES.PROMPT_TO_IMAGE]} credits = 1 ${DISPLAY_NAMES[TAB_NAMES.PROMPT_TO_IMAGE].toLowerCase()} generation`
                }
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Generation Form */}
            {selectedTab === TAB_NAMES.PROMPT_TO_IMAGE && (
              <div className={theme === "dark" ? "bg-gray-900 backdrop-blur-md shadow-xl rounded-2xl border border-gray-700" : "bg-white/90 backdrop-blur-md shadow-xl rounded-2xl border border-indigo-100"}>
                <div className="px-6 py-8">
                  <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-100 mb-4 flex items-center gap-2" : "text-2xl font-bold text-indigo-700 mb-4 flex items-center gap-2"}>
                    <svg className={theme === "dark" ? "w-6 h-6 text-gray-300" : "w-6 h-6 text-indigo-500"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                    Generate Image
                  </h3>
                  <form onSubmit={handleGenerate} encType="multipart/form-data" data-tab="prompt-to-image">
                    <div className="mb-4">
                      <textarea
                        className={theme === "dark" ? "w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none bg-gray-800 text-white text-base shadow" : "w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none bg-white/80 text-gray-900 text-base shadow"}
                        rows="4"
                        placeholder="Describe your dream image... (e.g., 'A neon-lit cyberpunk city at night with flying cars')"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        required
                      />
                    </div>
                    {/* PRO user options (always visible, disabled for non-pro) */}
                    <div className={theme === "dark" ? "mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800 p-4 rounded-xl border border-gray-700" : "mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-indigo-50/60 p-4 rounded-xl border border-indigo-100"}>
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-1" : "block text-sm font-medium text-indigo-700 mb-1"}>Go Fast</label>
                        <select className={theme === "dark" ? "w-full rounded-md border-gray-600 bg-gray-700 text-white" : "w-full rounded-md border-gray-300"} value={goFast} onChange={e => setGoFast(e.target.value === 'true')} disabled={!isProUser}>
                          <option value="true">No</option>
                          <option value="false">Yes</option>
                        </select>
                      </div>
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-1" : "block text-sm font-medium text-indigo-700 mb-1"}>Megapixels</label>
                        <select className={theme === "dark" ? "w-full rounded-md border-gray-600 bg-gray-700 text-white" : "w-full rounded-md border-gray-300"} value={megapixels} onChange={e => setMegapixels(e.target.value)} disabled={!isProUser}>
                          <option value="1">1</option>
                          <option value="2">0.25</option>
                        </select>
                      </div>
                      {/* <div>
                        <label className="block text-sm font-medium text-indigo-700 mb-1">Number of Outputs</label>
                        <input type="number" min="1" max="4" className="w-full rounded-md border-gray-300" value={numOutputs} onChange={e => setNumOutputs(Number(e.target.value))} disabled={!isProUser} />
                      </div> */}
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-1" : "block text-sm font-medium text-indigo-700 mb-1"}>Aspect Ratio</label>
                        <select className={theme === "dark" ? "w-full rounded-md border-gray-600 bg-gray-700 text-white" : "w-full rounded-md border-gray-300"} value={aspectRatio} onChange={e => setAspectRatio(e.target.value)} disabled={!isProUser}>
                          <option value="1:1">1:1</option>
                          <option value="16:9">16:9</option>
                          <option value="21:9">21:9</option>
                          <option value="3:2">3:2</option>
                          <option value="2:3">2:3</option>
                          <option value="4:5">4:5</option>
                          <option value="5:4">5:4</option>
                          <option value="3:4">3:4</option>
                          <option value="4:3">4:3</option>
                          <option value="9:16">9:16</option>
                          <option value="9:21">9:21</option>
                        </select>
                      </div>
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-1" : "block text-sm font-medium text-indigo-700 mb-1"}>Output Format</label>
                        <select className={theme === "dark" ? "w-full rounded-md border-gray-600 bg-gray-700 text-white" : "w-full rounded-md border-gray-300"} value={outputFormat} onChange={e => setOutputFormat(e.target.value)} disabled={!isProUser}>
                          <option value="webp">webp</option>
                          <option value="jpg">jpg</option>
                          <option value="png">png</option>
                        </select>
                      </div>
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-1" : "block text-sm font-medium text-indigo-700 mb-1"}>Output Quality</label>
                        <input type="number" min="10" max="100" className={theme === "dark" ? "w-full rounded-md border-gray-600 bg-gray-700 text-white" : "w-full rounded-md border-gray-300"} value={outputQuality} onChange={e => setOutputQuality(Number(e.target.value))} disabled={!isProUser} />
                      </div>
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-1" : "block text-sm font-medium text-indigo-700 mb-1"}>Inference Steps</label>
                        <input type="number" min="1" max="4" className={theme === "dark" ? "w-full rounded-md border-gray-600 bg-gray-700 text-white" : "w-full rounded-md border-gray-300"} value={numInferenceSteps} onChange={e => setNumInferenceSteps(Number(e.target.value))} disabled={!isProUser} />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading || getRemainingCredits() < 10}
                      className={theme === "dark" ? "w-full bg-gradient-to-r from-gray-700 via-gray-600 to-gray-800 text-white py-3 px-4 rounded-xl font-semibold hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200 border border-gray-600" : "w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white py-3 px-4 rounded-xl font-semibold hover:from-indigo-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200"}
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                          Generating...
                        </div>
                      ) : (
                        "Generate Image"
                      )}
                    </button>
                  </form>
                  {getRemainingCredits() < 10 && (
                    <div className={theme === "dark" ? "mt-4 p-4 bg-yellow-900 border border-yellow-700 rounded-xl shadow" : "mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl shadow"}>
                      <p className={theme === "dark" ? "text-yellow-200 text-sm" : "text-yellow-800 text-sm"}>
                        You need at least 10 credits to generate an image.{' '}
                        <button
                          onClick={() => navigate('/subscription')}
                          className={theme === "dark" ? "font-medium underline hover:no-underline text-blue-400" : "font-medium underline hover:no-underline text-indigo-600"}
                        >
                          Buy credits now
                        </button>{' '}
                        to continue creating amazing images!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {selectedTab === TAB_NAMES.TRANSFORM_IMAGE && (
              <div className={theme === "dark" ? "bg-gray-900 backdrop-blur-md shadow-xl rounded-2xl border border-gray-700" : "bg-white/90 backdrop-blur-md shadow-xl rounded-2xl border border-indigo-100"}>
                <div className="px-6 py-8">
                  <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-100 mb-4 flex items-center gap-2" : "text-2xl font-bold text-indigo-700 mb-4 flex items-center gap-2"}>
                    <svg className={theme === "dark" ? "w-6 h-6 text-gray-300" : "w-6 h-6 text-indigo-500"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                    {DISPLAY_NAMES[TAB_NAMES.TRANSFORM_IMAGE]}
                  </h3>
                  <form onSubmit={handleGenerate} encType="multipart/form-data" data-tab="transform-image">
                    <div className="mb-4" style={{ position: "relative" }}>
                      <textarea
                        className={theme === "dark" ? "w-full px-4 py-3 pr-16 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none bg-gray-800 text-white text-base shadow" : "w-full px-4 py-3 pr-16 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none bg-white/80 text-gray-900 text-base shadow"}
                        rows="4"
                        placeholder="Describe your changes to the input image... (e.g., 'Make it look cyberpunk style')"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        required
                        style={{ position: "relative" }}
                      />
                      {/* Input button at bottom left inside textarea container */}
                      <input
                        type="file"
                        accept="image/*"
                        id="input-image-file"
                        className="hidden"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          setInputImage(file || null);
                        }}
                      />
                      <label
                        htmlFor="input-image-file"
                        className={
                          theme === "dark"
                            ? "absolute bottom-2 left-2 cursor-pointer flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 text-pink-300 font-semibold shadow-lg border border-gray-700 hover:from-gray-800 hover:to-gray-900 transition-all duration-200"
                            : "absolute bottom-2 left-2 cursor-pointer flex items-center gap-2 px-3 py-2 rounded-full bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 text-indigo-700 font-semibold shadow-lg border border-indigo-200 hover:from-pink-100 hover:to-indigo-100 transition-all duration-200"
                        }
                        style={{ zIndex: 2 }}
                      >
                        {/* New icon: image/photo icon */}
                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-white/10 border border-gray-400">
                          <svg className={theme === "dark" ? "w-5 h-5 text-pink-300" : "w-5 h-5 text-indigo-500"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill={theme === "dark" ? "#1f2937" : "#fff"} />
                            <circle cx="8" cy="13" r="2" stroke={theme === "dark" ? "#f472b6" : "#6366f1"} strokeWidth="2" fill="none" />
                            <path d="M21 19l-5-6-4 5-3-4-4 5" stroke={theme === "dark" ? "#f472b6" : "#6366f1"} strokeWidth="2" fill="none" />
                          </svg>
                        </span>
                        <span className="ml-2 font-medium">
                          {inputImage ? <span className="truncate max-w-[120px]">{inputImage.name}</span> : "Add Image"}
                        </span>
                      </label>
                      {/* Show the input image preview next to the button if selected */}
                      {inputImage && (
                        <div className="absolute bottom-2 left-36 flex items-center z-1">
                          <img
                            src={URL.createObjectURL(inputImage)}
                            alt="preview"
                            className="w-10 h-10 object-cover rounded shadow border border-gray-700 mr-2"
                            style={{ pointerEvents: "none" }}
                          />
                        </div>
                      )}
                    </div>
                    {/* Transform Image options - only Aspect Ratio and Output Format */}
                    <div className={theme === "dark" ? "mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800 p-4 rounded-xl border border-gray-700" : "mb-4 grid grid-cols-1 md:grid-cols-2 gap-4 bg-indigo-50/60 p-4 rounded-xl border border-indigo-100"}>
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-1" : "block text-sm font-medium text-indigo-700 mb-1"}>Aspect Ratio</label>
                        <select className={theme === "dark" ? "w-full rounded-md border-gray-600 bg-gray-700 text-white" : "w-full rounded-md border-gray-300"} value={aspectRatio} onChange={e => setAspectRatio(e.target.value)}>
                          <option value="1:1">1:1</option>
                          <option value="16:9">16:9</option>
                          <option value="21:9">21:9</option>
                          <option value="3:2">3:2</option>
                          <option value="2:3">2:3</option>
                          <option value="4:5">4:5</option>
                          <option value="5:4">5:4</option>
                          <option value="3:4">3:4</option>
                          <option value="4:3">4:3</option>
                          <option value="9:16">9:16</option>
                          <option value="9:21">9:21</option>
                        </select>
                      </div>
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-1" : "block text-sm font-medium text-indigo-700 mb-1"}>Output Format</label>
                        <select className={theme === "dark" ? "w-full rounded-md border-gray-600 bg-gray-700 text-white" : "w-full rounded-md border-gray-300"} value={outputFormat} onChange={e => setOutputFormat(e.target.value)}>
                          <option value="jpg">JPG</option>
                          <option value="png">PNG</option>
                        </select>
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading || getRemainingCredits() < getCreditCost(TAB_NAMES.TRANSFORM_IMAGE)}
                      className={theme === "dark"
                        ? "w-full bg-gray-800 text-white py-3 px-4 rounded-xl font-semibold hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200 border border-gray-600"
                        : "w-full bg-white text-indigo-700 py-3 px-4 rounded-xl font-semibold hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200 border border-indigo-200"
                      }
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                          Generating...
                        </div>
                      ) : (
                        "Generate Image"
                      )}
                    </button>
                  </form>
                  {getRemainingCredits() < getCreditCost(TAB_NAMES.TRANSFORM_IMAGE) && (
                    <div className={theme === "dark" ? "mt-4 p-4 bg-yellow-900 border border-yellow-700 rounded-xl shadow" : "mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl shadow"}>
                      <p className={theme === "dark" ? "text-yellow-200 text-sm" : "text-yellow-800 text-sm"}>
                        You need at least {getCreditCost(TAB_NAMES.TRANSFORM_IMAGE)} credits to generate an image.{' '}
                        <button
                          onClick={() => navigate('/subscription')}
                          className={theme === "dark" ? "font-medium underline hover:no-underline text-blue-400" : "font-medium underline hover:no-underline text-indigo-600"}
                        >
                          Buy credits now
                        </button>{' '}
                        to continue creating amazing images!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {selectedTab === TAB_NAMES.IMAGE_TO_VIDEO && (
              <div className={theme === "dark" ? "bg-gray-900 backdrop-blur-md shadow-xl rounded-2xl border border-gray-700" : "bg-white/90 backdrop-blur-md shadow-xl rounded-2xl border border-indigo-100"}>
                <div className="px-6 py-8">
                  <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-100 mb-4 flex items-center gap-2" : "text-2xl font-bold text-indigo-700 mb-4 flex items-center gap-2"}>
                    <svg className={theme === "dark" ? "w-6 h-6 text-gray-300" : "w-6 h-6 text-indigo-500"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                    {DISPLAY_NAMES[TAB_NAMES.IMAGE_TO_VIDEO]}
                  </h3>
                  <form onSubmit={handleGenerate} encType="multipart/form-data" data-tab="image-to-video">
                    <div className="mb-4">
                      <textarea
                        className={theme === "dark" ? "w-full px-4 py-3 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none bg-gray-800 text-white text-base shadow" : "w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 resize-none bg-white/80 text-gray-900 text-base shadow"}
                        rows="4"
                        placeholder="Describe the video motion... (e.g., 'The ship is moving fast and the man's hair is waving')"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        required
                      />
                    </div>
                    
                    {/* Image Upload Section */}
                    <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-2" : "block text-sm font-medium text-indigo-700 mb-2"}>First Frame Image</label>
                        <input
                          type="file"
                          accept="image/*"
                          id="video-image-file"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files[0];
                            if (file) {
                              setInputImage(file);
                            }
                          }}
                        />
                        <label
                          htmlFor="video-image-file"
                          className={
                            theme === "dark"
                              ? "w-full cursor-pointer flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-800 text-gray-300 font-medium shadow border border-gray-600 hover:bg-gray-700 transition-all duration-200"
                              : "w-full cursor-pointer flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-50 text-indigo-700 font-medium shadow border border-indigo-200 hover:bg-indigo-100 transition-all duration-200"
                          }
                        >
                          <svg className={theme === "dark" ? "w-5 h-5 text-gray-300" : "w-5 h-5 text-indigo-500"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill={theme === "dark" ? "#1f2937" : "#fff"} />
                            <circle cx="8" cy="13" r="2" stroke={theme === "dark" ? "#d1d5db" : "#6366f1"} strokeWidth="2" fill="none" />
                            <path d="M21 19l-5-6-4 5-3-4-4 5" stroke={theme === "dark" ? "#d1d5db" : "#6366f1"} strokeWidth="2" fill="none" />
                          </svg>
                          {inputImage ? <span className="truncate">{inputImage.name}</span> : "Upload First Frame"}
                        </label>
                      </div>
                      
                      <div>
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-2" : "block text-sm font-medium text-indigo-700 mb-2"}>Last Frame Image (Optional)</label>
                        <input
                          type="file"
                          accept="image/*"
                          id="video-last-image-file"
                          className="hidden"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            setLastImage(file || null);
                          }}
                        />
                        <label
                          htmlFor="video-last-image-file"
                          className={
                            theme === "dark"
                              ? "w-full cursor-pointer flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-800 text-gray-300 font-medium shadow border border-gray-600 hover:bg-gray-700 transition-all duration-200"
                              : "w-full cursor-pointer flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-50 text-indigo-700 font-medium shadow border border-indigo-200 hover:bg-indigo-100 transition-all duration-200"
                          }
                        >
                          <svg className={theme === "dark" ? "w-5 h-5 text-gray-300" : "w-5 h-5 text-indigo-500"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2" fill={theme === "dark" ? "#1f2937" : "#fff"} />
                            <circle cx="8" cy="13" r="2" stroke={theme === "dark" ? "#d1d5db" : "#6366f1"} strokeWidth="2" fill="none" />
                            <path d="M21 19l-5-6-4 5-3-4-4 5" stroke={theme === "dark" ? "#d1d5db" : "#6366f1"} strokeWidth="2" fill="none" />
                          </svg>
                          {lastImage ? <span className="truncate">{lastImage.name}</span> : "Upload Last Frame"}
                        </label>
                      </div>
                    </div>

                    {/* Full image previews for Transform to Video tab - compact display */}
                    {(inputImage || lastImage) && (
                      <div className="mb-4">
                        <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-2" : "block text-sm font-medium text-indigo-700 mb-2"}>
                          Selected Image Previews
                        </label>
                        <div className="space-y-4">
                          {inputImage && (
                            <div>
                              <p className={theme === "dark" ? "text-xs text-gray-400 mb-1" : "text-xs text-gray-600 mb-1"}>First Frame:</p>
                              <div className="flex justify-center">
                                <img
                                  src={URL.createObjectURL(inputImage)}
                                  alt="First frame full preview"
                                  className="max-w-full max-h-32 object-contain rounded-lg border shadow-sm"
                                  style={{ maxWidth: "400px" }}
                                />
                              </div>
                            </div>
                          )}
                          {lastImage && (
                            <div>
                              <p className={theme === "dark" ? "text-xs text-gray-400 mb-1" : "text-xs text-gray-600 mb-1"}>Last Frame:</p>
                              <div className="flex justify-center">
                                <img
                                  src={URL.createObjectURL(lastImage)}
                                  alt="Last frame full preview"
                                  className="max-w-full max-h-32 object-contain rounded-lg border shadow-sm"
                                  style={{ maxWidth: "400px" }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Video Creation options - only Resolution */}
                    <div className={theme === "dark" ? "mb-4 bg-gray-800 p-4 rounded-xl border border-gray-700" : "mb-4 bg-indigo-50/60 p-4 rounded-xl border border-indigo-100"}>
                      <label className={theme === "dark" ? "block text-sm font-medium text-gray-300 mb-1" : "block text-sm font-medium text-indigo-700 mb-1"}>Resolution</label>
                      <select className={theme === "dark" ? "w-full rounded-md border-gray-600 bg-gray-700 text-white" : "w-full rounded-md border-gray-300"} value={resolution} onChange={e => setResolution(e.target.value)}>
                        <option value="480p">480p</option>
                        <option value="720p">720p</option>
                      </select>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={loading || getRemainingCredits() < getCreditCost(TAB_NAMES.IMAGE_TO_VIDEO, resolution)}
                      className={theme === "dark"
                        ? "w-full bg-gray-800 text-white py-3 px-4 rounded-xl font-semibold hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200 border border-gray-600"
                        : "w-full bg-white text-indigo-700 py-3 px-4 rounded-xl font-semibold hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-200 border border-indigo-200"
                      }
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                          Creating Video...
                        </div>
                      ) : (
                        "Create Video"
                      )}
                    </button>
                  </form>
                  {getRemainingCredits() < getCreditCost(TAB_NAMES.IMAGE_TO_VIDEO, resolution) && (
                    <div className={theme === "dark" ? "mt-4 p-4 bg-yellow-900 border border-yellow-700 rounded-xl shadow" : "mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl shadow"}>
                      <p className={theme === "dark" ? "text-yellow-200 text-sm" : "text-yellow-800 text-sm"}>
                        You need at least {getCreditCost(TAB_NAMES.IMAGE_TO_VIDEO, resolution)} credits to create a video.{' '}
                        <button
                          onClick={() => navigate('/subscription')}
                          className={theme === "dark" ? "font-medium underline hover:no-underline text-blue-400" : "font-medium underline hover:no-underline text-indigo-600"}
                        >
                          Buy credits now
                        </button>{' '}
                        to continue creating amazing videos!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Generated Image */}
            {generatedImage && (
              <div className={theme === "dark" ? "bg-gray-900 backdrop-blur-md shadow-xl rounded-2xl border border-gray-700" : "bg-white/90 backdrop-blur-md shadow-xl rounded-2xl border border-pink-100"}>
                <div className="px-6 py-8">
                  <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-100 mb-4 flex items-center gap-2" : "text-2xl font-bold text-pink-700 mb-4 flex items-center gap-2"}>
                    <svg className={theme === "dark" ? "w-6 h-6 text-gray-300" : "w-6 h-6 text-pink-500"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                    Latest Generation
                  </h3>
                  {/* Professional note about image expiry */}
                  <div className={theme === "dark" ? "mb-4 p-3 bg-yellow-900 border border-yellow-700 rounded-xl text-yellow-200 text-sm text-center font-medium" : "mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-900 text-sm text-center font-medium"}>
                    Please make sure to download your generated image promptly, as it will expire soon and become unavailable for download.
                  </div>
                  <div className="space-y-4">
                    <div 
                      className="cursor-pointer group"
                      onClick={() => handleImageClick(generatedImage)}
                    >
                      {isVideo(generatedImage.image_url) ? (
                        <div className="relative">
                          <video
                            src={generatedImage.image_url}
                            className="w-full rounded-xl shadow-md group-hover:shadow-lg transition-shadow duration-200 border"
                            controls
                            autoPlay
                            loop
                            muted
                            onError={(e) => {
                              console.error("Video failed to load:", e);
                            }}
                          />
                          {/* Play icon overlay for latest generation video */}
                          <div className="absolute top-4 right-4 bg-black bg-opacity-60 rounded-full p-2">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={generatedImage.image_url || "/placeholder.svg"}
                          alt={generatedImage.prompt}
                          className="w-full rounded-xl shadow-md group-hover:shadow-lg transition-shadow duration-200 border"
                          onError={(e) => {
                            if (!e.target.src.endsWith('/placeholder.svg')) {
                              e.target.src = "/placeholder.svg";
                            }
                          }}
                        />
                      )}
                      <div className="mt-2 text-center">
                        <p className={theme === "dark" ? "text-sm text-gray-300 font-medium" : "text-sm text-pink-600 font-medium"}>
                          {isVideo(generatedImage.image_url) ? "Click to view video in full screen" : "Click to view in canvas"}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={async () => await downloadFile(generatedImage.image_url, `generated-${isVideo(generatedImage.image_url) ? 'video' : 'image'}-${Date.now()}.${isVideo(generatedImage.image_url) ? 'mp4' : 'jpg'}`)}
                        className={theme === "dark" ? "flex-1 bg-blue-800 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors" : "flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"}
                      >
                        Download {isVideo(generatedImage.image_url) ? 'Video' : 'Image'}
                      </button>
                    </div>
                    <div>
                      <p className={theme === "dark" ? "text-sm text-gray-300 font-medium" : "text-sm text-gray-600 font-medium"}>Prompt:</p>
                      <p className={theme === "dark" ? "text-sm text-gray-400" : "text-sm text-gray-800"}>{generatedImage.prompt}</p>
                    </div>
                    <div className={theme === "dark" ? "text-xs text-gray-500" : "text-xs text-gray-500"}>
                      Generated on {new Date(generatedImage.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Previous Generations */}
          {generations.length > 0 && (
            <div className={theme === "dark" ? "mt-10 bg-gray-900 backdrop-blur-md shadow-xl rounded-2xl border border-gray-700" : "mt-10 bg-white/90 backdrop-blur-md shadow-xl rounded-2xl border border-indigo-100"}>
              <div className="px-6 py-8">
                <h3 className={theme === "dark" ? "text-2xl font-bold text-gray-100 mb-4 flex items-center gap-2" : "text-2xl font-bold text-indigo-700 mb-4 flex items-center gap-2"}>
                  <svg className={theme === "dark" ? "w-6 h-6 text-gray-300" : "w-6 h-6 text-indigo-500"} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                  Previous Generations
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {generations.map((generation) => {
                    const isVideoFile = isVideo(generation.image_url);
                    // Debug logging
                    console.log('Generation:', generation.id, 'URL:', generation.image_url, 'isVideo:', isVideoFile);
                    
                    // For videos, ignore brokenImages state and always show if URL exists
                    // For images, use the existing brokenImages logic
                    const isContentAvailable = generation.image_url && (isVideoFile || !brokenImages[generation.id]);
                    
                    return (
                      <div
                        key={generation.id}
                        className={
                          (theme === "dark" ? 'border border-gray-700 rounded-xl overflow-hidden hover:shadow-lg transition-shadow group bg-gray-800 cursor-pointer' : 'border border-gray-100 rounded-xl overflow-hidden hover:shadow-lg transition-shadow group bg-white/80 cursor-pointer') +
                          (isContentAvailable ? '' : ' opacity-60')
                        }
                        onClick={() => handleImageClick(generation)}
                        tabIndex={0}
                        aria-disabled={!isContentAvailable}
                      >
                        <div className="relative">
                          {isContentAvailable ? (
                            <>
                              {isVideoFile ? (
                                <div className="relative">
                                  <video
                                    src={generation.image_url}
                                    className="w-full h-48 object-cover group-hover:opacity-90 transition-opacity rounded-t-xl"
                                    muted
                                    loop
                                    preload="metadata"
                                    onLoadStart={() => console.log('Video loading started:', generation.image_url)}
                                    onCanPlay={() => console.log('Video can play:', generation.image_url)}
                                    onError={(e) => {
                                      console.error("Video failed to load:", generation.image_url, e);
                                      setBrokenImages(prev => ({ ...prev, [generation.id]: true }));
                                    }}
                                  />
                                  {/* Play icon overlay for videos */}
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="bg-black bg-opacity-50 rounded-full p-3 group-hover:bg-opacity-70 transition-all duration-200">
                                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M8 5v14l11-7z"/>
                                      </svg>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <img
                                  src={generation.image_url}
                                  alt={generation.prompt}
                                  className="w-full h-48 object-cover group-hover:opacity-90 transition-opacity rounded-t-xl"
                                  onError={() => setBrokenImages(prev => ({ ...prev, [generation.id]: true }))}
                                />
                              )}
                            </>
                          ) : (
                            <div className={theme === "dark" ? "w-full h-48 flex items-center justify-center bg-gray-700 rounded-t-xl text-gray-400 text-center px-4" : "w-full h-48 flex items-center justify-center bg-gray-100 rounded-t-xl text-gray-500 text-center px-4"}>
                              <span>
                                This {isVideoFile ? 'video' : 'image'} is no longer available for download or viewing.<br />
                                However, the original prompt is still shown below for your reference.
                              </span>
                            </div>
                          )}
                          {isContentAvailable && (
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center rounded-t-xl">
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                {isVideoFile ? (
                                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                ) : (
                                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <p className={theme === "dark" ? "text-sm text-gray-300 truncate" : "text-sm text-gray-700 truncate"} title={generation.prompt}>
                            {generation.prompt}
                          </p>
                          <div className="flex justify-between items-center mt-2">
                            <p className={theme === "dark" ? "text-xs text-gray-500" : "text-xs text-gray-400"}>
                              {new Date(generation.created_at).toLocaleDateString()}
                            </p>
                            <div className="flex gap-2">
                              {isContentAvailable && (
                                <button
                                  className={theme === "dark" ? "text-xs text-blue-400 hover:text-blue-300 font-semibold px-2 py-1 rounded transition-colors" : "text-xs text-blue-500 hover:text-blue-700 font-semibold px-2 py-1 rounded transition-colors"}
                                  onClick={async e => {
                                    e.stopPropagation();
                                    await downloadFile(generation.image_url, `${isVideoFile ? 'video' : 'image'}-${generation.id}.${isVideoFile ? 'mp4' : 'jpg'}`);
                                  }}
                                >
                                  Download
                                </button>
                              )}
                              <button
                                className={theme === "dark" ? "text-xs text-red-400 hover:text-red-300 font-semibold px-2 py-1 rounded transition-colors" : "text-xs text-red-500 hover:text-red-700 font-semibold px-2 py-1 rounded transition-colors"}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteGeneration(generation.id);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Image Viewer Modal */}
      {selectedImage && (
        <ImageViewer 
          image={selectedImage} 
          onClose={closeImageViewer} 
          brokenImages={brokenImages}
        />
      )}
      
      {/* Toast Notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => hideToast(toast.id)}
          duration={0} // Duration handled by useToast hook
        />
      ))}
    </div>
  )
}

export default Dashboard
